import classNames from "classnames";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import CuratedSearchFilterJobTitleSection from "./CuratedSearchFilterSection/CuratedSearchFilterJobTitleSection";
import { useContext } from "react";
import CuratedSearchFilterCompanyHeadcountSection from "./CuratedSearchFilterSection/LinkedinSalesNavigatorFilters/CuratedSearchFilterCompanyHeadcountSection";
import CuratedSearchFilterCurrentCompanySection from "./CuratedSearchFilterSection/CuratedSearchFilterCurrentCompanySection";
import CuratedSearchFilterIndustrySection from "./CuratedSearchFilterSection/LinkedinSalesNavigatorFilters/CuratedSearchFilterIndustrySection";
import CuratedSearchFilterGeographySection from "./CuratedSearchFilterSection/LinkedinSalesNavigatorFilters/CuratedSearchFilterGeographySection";
import CuratedSearchFilterCompanyHqSection from "./CuratedSearchFilterSection/LinkedinSalesNavigatorFilters/CuratedSearchFilterCompanyHqSection";
import { toast } from "react-hot-toast";
import {
  getScoringCriteriaErrors,
  hasFiltersApplied,
} from "../curatedSearchUtils";
import { AdjustmentsVerticalIcon } from "@heroicons/react/20/solid";
import CuratedSearchFilterKeyWordsSection from "./CuratedSearchFilterSection/CuratedSearchFilterKeyWordsSection";
import sendPayloadToExtension from "../../../utils/sendPayloadToExtension";
import { CHECK_EXTENSION_INSTALLED } from "../../../utils/sendPayloadToExtension";
import CiroLink from "../../shared/CiroLink";
import AutopilotCuratedSearchContext from "../../../contexts/AutopilotCuratedSearchContext";
import AutopilotCuratedLinkedinSalesNavigatorSearchContext from "../../../contexts/AutopilotCuratedLinkedinSalesNavigatorSearchContext";

const CuratedLinkedinSalesNavigatorSearchFiltersCard = () => {
  const { scoringCriteriaValues } = useContext(AutopilotCuratedSearchContext);

  const { onSaveFilters, savedFilters, setSavedFilters } = useContext(
    AutopilotCuratedLinkedinSalesNavigatorSearchContext,
  );

  const scoringCriteriaErrors = getScoringCriteriaErrors(scoringCriteriaValues);

  const hasScoringCriteriaErrors = scoringCriteriaErrors.some(
    (error) => error !== null,
  );

  return (
    <div className={classNames("w-full", "pt-4")}>
      {/* Header */}
      <div
        className={classNames(
          "flex",
          "justify-between",
          "items-center",
          "mb-4",
        )}
      >
        <div className={classNames("flex", "items-center", "gap-2")}>
          <AdjustmentsVerticalIcon
            className={classNames("h-5", "w-5", "text-orange-500")}
          />
          <h2 className={classNames("text-lg", "font-semibold")}>
            Selected Filters
          </h2>
        </div>
      </div>

      {/* Filters */}
      <div
        className={classNames("grid", "grid-cols-1", "xl:grid-cols-2", "gap-2")}
      >
        <CuratedSearchFilterKeyWordsSection
          defaultOpen={false}
          currentKeyWordsFilter={savedFilters?.keywords || ""}
          setCurrentKeyWordsFilter={(v) => {
            setSavedFilters({ ...(savedFilters || {}), keywords: v });
          }}
        />
        <CuratedSearchFilterJobTitleSection
          defaultOpen={false}
          jobTitleFilter={savedFilters?.jobTitles || []}
          setJobTitleFilter={(v) => {
            setSavedFilters({
              ...(savedFilters || {}),
              jobTitles: v,
            });
          }}
        />
        <CuratedSearchFilterCurrentCompanySection
          defaultOpen={false}
          currentCompanyFilter={
            (savedFilters?.companyNames ?? []) as {
              label: string;
            }[]
          }
          excludedCompanyFilter={
            (savedFilters?.excludedCompanyNames ?? []) as {
              label: string;
            }[]
          }
          setCurrentCompanyFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              companyNames: v,
            });
          }}
          setExcludedCompanyFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              excludedCompanyNames: v,
            });
          }}
        />
        <CuratedSearchFilterCompanyHeadcountSection
          defaultOpen={false}
          companyHeadcountFilter={
            (savedFilters?.companyHeadCounts || []) as {
              id: string;
              label: string;
            }[]
          }
          setCompanyHeadcountFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              companyHeadCounts: v,
            });
          }}
        />
        <CuratedSearchFilterCompanyHqSection
          defaultOpen={false}
          currentCompanyHqFilter={
            (savedFilters?.companyHQLocations ?? []) as {
              label: string;
              id: number;
            }[]
          }
          setCurrentCompanyHqFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              companyHQLocations: v,
            });
          }}
        />
        <CuratedSearchFilterGeographySection
          defaultOpen={false}
          currentGeographyFilter={
            (savedFilters?.geographies ?? []) as {
              label: string;
              id: number;
            }[]
          }
          setCurrentGeographyFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              geographies: v,
            });
          }}
        />
        <CuratedSearchFilterIndustrySection
          defaultOpen={false}
          currentIndustryFilter={
            (savedFilters?.industries ?? []) as {
              label: string;
              id: number;
            }[]
          }
          setCurrentIndustryFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              industries: v,
            });
          }}
        />
      </div>

      {/* Footer */}
      <div className={classNames("flex", "justify-end", "gap-2", "mt-6")}>
        <CiroButton
          analyticsField="Save Sales Navigator Filters"
          style={CiroButtonStyleEnum.LOUD}
          disabled={!hasFiltersApplied({ filters: savedFilters })}
          onClick={async () => {
            if (!savedFilters) {
              toast.error("No filters applied");
              return;
            }
            if (hasScoringCriteriaErrors) {
              toast.error("Please fix errors in scoring criteria");
              return;
            }
            try {
              await sendPayloadToExtension({
                payload: {
                  action: CHECK_EXTENSION_INSTALLED,
                },
              });
              await onSaveFilters({
                currentFilters: savedFilters,
                scoringCriteriaValues,
              });
            } catch (error) {
              toast.error(
                <div>
                  <div>Chrome extension not found</div>
                  <div>
                    <CiroLink href="https://chromewebstore.google.com/detail/ciro/poiofceiibpjdmhihnpaembimmnioifk">
                      Download it here
                    </CiroLink>
                  </div>
                </div>,
              );
            }
          }}
        >
          Launch Sales Navigator
        </CiroButton>
      </div>
    </div>
  );
};

export default CuratedLinkedinSalesNavigatorSearchFiltersCard;
